import { useMemo, useState } from 'react'
import type { OverviewStore } from '../api/overview'
import { formatNumber } from '../lib/format'

interface AdherenceRow {
  code: string
  name: string
  worked: number
  grid: number
  variance: number
  variancePct: number
  absPct: number
  absHours: number
}

function signedPercent(value: number): string {
  const sign = value < 0 ? '−' : value > 0 ? '+' : ''
  return `${sign}${formatNumber(Math.abs(value), 1)}%`
}

function signedHours(value: number): string {
  const sign = value < 0 ? '−' : value > 0 ? '+' : ''
  return `${sign}${formatNumber(Math.abs(value), 1)} h`
}

function toneFor(value: number): { text: string; bar: string } {
  if (value < 0) {
    return {
      text: 'text-emerald-700 dark:text-emerald-300',
      bar: 'bg-emerald-500',
    }
  }
  if (value > 0) {
    return {
      text: 'text-rose-700 dark:text-rose-300',
      bar: 'bg-rose-500',
    }
  }
  return {
    text: 'text-slate-500 dark:text-slate-400',
    bar: 'bg-slate-400',
  }
}

interface GridAdherenceChartProps {
  stores: OverviewStore[]
  /** Number of shops requested, so the badge can say “17 of 19 shops”. */
  requested?: number
}

/**
 * Interactive grid-adherence bars for the General Overview.
 *
 * Adherence is the absolute percentage deviation from the COL grid:
 * ``|worked - grid| / grid``. Shops closest to their grid come first; ties
 * break on absolute hours and then shop number. The bar is signed so a shop
 * below grid extends left (green) and one above extends right (red).
 *
 * Only shops with both figures are charted — a missing grid or missing
 * Shiftbase hours cannot be ranked. Hover/focus previews a shop, click pins it.
 */
export function GridAdherenceChart({ stores, requested }: GridAdherenceChartProps) {
  const [hovered, setHovered] = useState<string | null>(null)
  const [pinned, setPinned] = useState<string | null>(null)

  const rows = useMemo<AdherenceRow[]>(() => {
    return stores
      .filter(
        (store) =>
          store.worked_hours != null &&
          store.grid_hours != null &&
          store.grid_hours > 0,
      )
      .map((store) => {
        const worked = store.worked_hours as number
        const grid = store.grid_hours as number
        const variance = worked - grid
        const variancePct = (variance / grid) * 100
        return {
          code: store.store_code,
          name: store.store_name,
          worked,
          grid,
          variance,
          variancePct,
          absPct: Math.abs(variancePct),
          absHours: Math.abs(variance),
        }
      })
      .sort(
        (a, b) =>
          a.absPct - b.absPct ||
          a.absHours - b.absHours ||
          a.code.localeCompare(b.code),
      )
  }, [stores])

  const maxAbsPct = rows.reduce((max, row) => Math.max(max, row.absPct), 0)
  const activeCode = hovered ?? pinned
  const active = rows.find((row) => row.code === activeCode) ?? rows[0] ?? null
  const highlightedCode = activeCode ?? active?.code ?? null
  const togglePinned = (code: string) => {
    setPinned((current) => (current === code ? null : code))
  }

  if (rows.length < 2) return null

  return (
    <section className="card space-y-3 p-4" aria-label="Grid adherence by shop">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Grid adherence
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Closest % deviation from the grid first; the bar shows worked vs grid for
            the period.
          </p>
        </div>
        <span className="badge bg-slate-500/10 text-slate-600 dark:bg-white/5 dark:text-slate-300">
          {requested && requested !== rows.length
            ? `${rows.length} of ${requested} shops`
            : `${rows.length} shop${rows.length === 1 ? '' : 's'}`}
        </span>
      </header>

      {active && (
        <p
          className="rounded-xl bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-600 dark:bg-white/5 dark:text-slate-300"
        >
          <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
            #{active.code}
          </span>{' '}
          <span className="font-semibold text-slate-800 dark:text-slate-100">
            {active.name}
          </span>{' '}
          · {formatNumber(active.worked, 1)} h worked vs {formatNumber(active.grid, 1)} h
          grid ·{' '}
          <span className={toneFor(active.variancePct).text}>
            {signedHours(active.variance)} ({signedPercent(active.variancePct)})
          </span>
        </p>
      )}

      <ol className="space-y-0.5">
        {rows.map((row, index) => {
          const tone = toneFor(row.variancePct)
          const width = maxAbsPct > 0 ? (row.absPct / maxAbsPct) * 50 : 0
          const isActive = highlightedCode === row.code
          return (
            <li key={row.code}>
              <button
                type="button"
                onClick={() => togglePinned(row.code)}
                onMouseEnter={() => setHovered(row.code)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(row.code)}
                onBlur={() => setHovered(null)}
                aria-pressed={pinned === row.code}
                aria-label={`${row.code} ${row.name}: ${formatNumber(
                  row.worked,
                  1,
                )} hours worked vs ${formatNumber(row.grid, 1)} hours grid, ${signedHours(
                  row.variance,
                )} (${signedPercent(row.variancePct)})`}
                title={`${row.name} · ${formatNumber(row.worked, 1)} h worked vs ${formatNumber(
                  row.grid,
                  1,
                )} h grid`}
                className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left text-[11px] transition-colors ${
                  isActive
                    ? 'bg-slate-100 dark:bg-white/10'
                    : 'hover:bg-slate-50 dark:hover:bg-white/5'
                }`}
              >
                <span className="w-4 shrink-0 text-right font-mono text-[9px] text-slate-400 dark:text-slate-500">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="w-10 shrink-0 font-mono text-[10px] text-slate-500 dark:text-slate-400">
                  #{row.code}
                </span>
                <span
                  className="relative h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10"
                  aria-hidden="true"
                >
                  <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-slate-300 dark:bg-white/20" />
                  {row.absPct > 0 ? (
                    <span
                      className={`absolute top-0 h-full rounded-full ${tone.bar} ${
                        row.variance < 0 ? 'right-1/2' : 'left-1/2'
                      }`}
                      style={{ width: `${width}%` }}
                    />
                  ) : (
                    <span className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-400" />
                  )}
                </span>
                <span
                  className={`w-12 shrink-0 text-right tabular-nums ${tone.text}`}
                >
                  {signedPercent(row.variancePct)}
                </span>
              </button>
            </li>
          )
        })}
      </ol>

      <p className="text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">
        Green = below grid · red = above grid · click a shop to pin it. Only shops
        with both worked hours and a positive grid are ranked.
      </p>
    </section>
  )
}
